import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import PdfPipeline from "./robo/PdfPipeline";
import { Select, StatusPill, pluralise } from "./robo/ui";

// ── Info dot ───────────────────────────────────────────────────────────────────

// A hover explanation for a label that needs one. Used on the tab bar to say which
// tabs are authoring tools that only make sense on your own machine.
const InfoDot = ({ label, children, align = "left" }) => (
  <span className="relative inline-flex group align-middle">
    <span
      aria-label={label}
      className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border border-white/30
                 text-[9px] font-semibold text-white/50 cursor-help select-none
                 group-hover:text-white/80 group-hover:border-white/50 transition-colors"
    >
      i
    </span>
    <span
      role="tooltip"
      className={`pointer-events-none absolute ${align === "right" ? "right-0" : "left-0"} top-full z-30 mt-2 w-72
                  rounded-lg border border-white/15 bg-[#0b1c27] px-3 py-2.5 text-[11px] leading-relaxed
                  text-left font-normal normal-case tracking-normal text-white/70 shadow-xl
                  opacity-0 invisible translate-y-1
                  group-hover:opacity-100 group-hover:visible group-hover:translate-y-0
                  transition-all duration-150`}
    >
      {children}
    </span>
  </span>
);

// ── Constants ──────────────────────────────────────────────────────────────────

// Subjects + exam types are loaded from the backend (GET /api/admin/robo/options)
// so the dropdowns stay locked to what the parser + DB actually support. These
// are only fallbacks used if that request fails or returns nothing.
const FALLBACK_SUBJECTS   = ["Mathematics", "Biology"];
const FALLBACK_EXAM_TYPES = ["BGCSE"];
const PAPERS = ["1", "2", "3"];
const YEARS = Array.from({ length: 20 }, (_, i) => String(2026 - i)); // 2026 → 2007

// ── Helpers ────────────────────────────────────────────────────────────────────

const FileIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

// ── Sub-components ─────────────────────────────────────────────────────────────

// ── ImageUploadRow ─────────────────────────────────────────────────────────────
// One row per pending image. Saves the file to disk only — DB attachment
// happens later when Import runs JsonQuestionImportService.
// Calls onSaved(filename) so the parent can gate the Import button.

const ImageUploadRow = ({ item, subject, examType, year, paper, onSaved, isSaved, wasAlreadyOnDisk }) => {
  const [file,         setFile]         = useState(null);
  const [status,       setStatus]       = useState(isSaved ? "done" : "idle"); // idle | uploading | done | error
  const [errMsg,       setErrMsg]       = useState("");
  const [foundOnDisk,  setFoundOnDisk]  = useState(wasAlreadyOnDisk);
  const [recheckState, setRecheckState] = useState("idle"); // idle | checking | not_found
  const [checkedPath,  setCheckedPath]  = useState("");
  const inputRef = useRef(null);

  const handleUpload = async () => {
    if (!file) return;
    setStatus("uploading");
    setErrMsg("");

    try {
      const formData = new FormData();
      formData.append("subject",  subject);
      formData.append("examType", examType);
      formData.append("year",     year);
      formData.append("paper",    paper);
      formData.append("filename", item.filename);
      formData.append("image",    file);

      await axios.post(`${API_BASE_URL}/api/admin/robo/save_image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setStatus("done");
      setFoundOnDisk(false); // it was uploaded, not pre-existing
      onSaved?.(item.filename);
    } catch (err) {
      setErrMsg(err?.response?.data?.error || err.message);
      setStatus("error");
    }
  };

  const handleRecheck = async () => {
    setRecheckState("checking");
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/admin/robo/check_image`, {
        params: { subject, examType, year, paper, filename: item.filename },
      });
      if (data.exists) {
        setStatus("done");
        setFoundOnDisk(true);
        setRecheckState("idle");
        onSaved?.(item.filename);
      } else {
        setCheckedPath(data.checked_path || "");
        setRecheckState("not_found");
      }
    } catch (err) {
      setRecheckState("idle");
      setErrMsg(err?.response?.data?.error || err.message);
    }
  };

  return (
    <div className={`rounded-lg border px-4 py-3 space-y-2 transition-colors ${
      status === "done"
        ? "bg-emerald-500/5 border-emerald-500/20"
        : "bg-white/5 border-white/10"
    }`}>
      <div className="flex items-start gap-3">
        <span className="rounded-md bg-sky-500/10 border border-sky-500/20
                         px-2 py-0.5 font-mono text-sky-300 text-xs shrink-0 mt-0.5">
          Q{item.number}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-white/80 font-mono">{item.filename}</p>
          <p className="text-xs text-white/40 mt-0.5">{item.description}</p>
        </div>
        {status === "done" && <span className="text-emerald-400 text-lg">✓</span>}
      </div>

      {status !== "done" && (
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            className="hidden"
            onChange={(e) => setFile(e.target.files[0] || null)}
          />
          <button
            onClick={() => inputRef.current?.click()}
            className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs
                       text-white/60 hover:bg-white/10 transition-colors truncate max-w-[180px]"
          >
            {file ? file.name : "Choose image…"}
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || status === "uploading"}
            className="rounded-lg bg-sky-600 hover:bg-sky-500 px-3 py-1.5 text-xs font-medium
                       text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            {status === "uploading" ? "Saving…" : "Save to disk"}
          </button>

          {/* Manual recheck — for files added directly to the repo (Finder, scp, etc.) */}
          <button
            onClick={handleRecheck}
            disabled={recheckState === "checking"}
            title="Re-check the disk for this image (e.g. if you added it manually)"
            className="rounded-lg border border-white/15 bg-transparent px-3 py-1.5 text-xs
                       text-white/50 hover:text-white/80 hover:bg-white/5
                       disabled:opacity-40 transition-colors shrink-0"
          >
            {recheckState === "checking" ? "Checking…" : "↻ Recheck"}
          </button>

          {recheckState === "not_found" && (
            <div className="w-full space-y-1">
              <p className="text-xs text-amber-400">
                Not on disk. Expected exactly here:
              </p>
              <code className="block text-[11px] text-amber-200/80 bg-black/30 rounded px-2 py-1 font-mono break-all">
                {checkedPath}
              </code>
              <p className="text-[11px] text-white/40">
                Filenames are case-sensitive — e.g. <code className="text-white/60">q5.png</code> ≠ <code className="text-white/60">Q5.png</code>.
              </p>
            </div>
          )}

          {status === "error" && (
            <p className="text-xs text-red-400 break-words w-full">{errMsg}</p>
          )}
        </div>
      )}

      {status === "done" && (
        <p className="text-xs text-emerald-400">
          {foundOnDisk
            ? "Already on disk ✓ — no upload needed"
            : "Saved to disk ✓ — will be attached when the JSON is built"}
        </p>
      )}
    </div>
  );
};

// ── PapersPanel ────────────────────────────────────────────────────────────────
// Lists every JSON paper in the repo with its DB sync status and a per-paper
// import button. This is the primary surface for "import a finished paper" —
// independent of the build flow below.

const PapersPanel = ({ refreshKey }) => {
  const [papers,    setPapers]    = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [importing, setImporting] = useState(null);
  const [errMsg,    setErrMsg]    = useState("");

  // ── Filter state ─────────────────────────────────────────────────────────────
  const [statusFilter,   setStatusFilter]   = useState("all");      // all | needs_import | in_sync | empty
  const [subjectFilter,  setSubjectFilter]  = useState("all");
  const [examTypeFilter, setExamTypeFilter] = useState("all");
  const [yearFilter,     setYearFilter]     = useState("all");
  const [search,         setSearch]         = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrMsg("");
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/admin/robo/papers_status`);
      setPapers(data.papers || []);
    } catch (err) {
      setErrMsg(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleImport = async (paper) => {
    const key = `${paper.subject}-${paper.year}-${paper.paper}`;
    setImporting(key);
    setErrMsg("");
    try {
      await axios.post(`${API_BASE_URL}/api/admin/robo/import_db`, {
        subject:  paper.subject,
        examType: paper.exam_type,
        year:     paper.year,
        paper:    paper.paper,
      });
      await load();
    } catch (err) {
      setErrMsg(err?.response?.data?.error || err.message);
    } finally {
      setImporting(null);
    }
  };

  // ── Derived: dropdown options pulled from the actual data ────────────────────
  const subjectOptions  = ["all", ...Array.from(new Set(papers.map((p) => p.subject))).sort()];
  const examTypeOptions = ["all", ...Array.from(new Set(papers.map((p) => p.exam_type))).sort()];
  const yearOptions     = ["all", ...Array.from(new Set(papers.map((p) => p.year))).sort((a, b) => Number(b) - Number(a))];

  // ── Status counts (always reflect the FULL list, not filtered) ───────────────
  const statusCounts = papers.reduce((acc, p) => {
    acc.all++;
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, { all: 0, needs_import: 0, in_sync: 0, empty: 0 });

  // ── Apply all filters ────────────────────────────────────────────────────────
  const filteredPapers = papers.filter((p) => {
    if (statusFilter   !== "all" && p.status    !== statusFilter)   return false;
    if (subjectFilter  !== "all" && p.subject   !== subjectFilter)  return false;
    if (examTypeFilter !== "all" && p.exam_type !== examTypeFilter) return false;
    if (yearFilter     !== "all" && p.year      !== yearFilter)     return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const haystack = `${p.subject} ${p.exam_type} ${p.year} paper ${p.paper} ${p.json_path}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const anyFilterActive = statusFilter !== "all" || subjectFilter !== "all"
    || examTypeFilter !== "all" || yearFilter !== "all" || search.trim() !== "";

  const clearFilters = () => {
    setStatusFilter("all");
    setSubjectFilter("all");
    setExamTypeFilter("all");
    setYearFilter("all");
    setSearch("");
  };

  // ── Status pill button helper ────────────────────────────────────────────────
  const statusButton = (value, label, color) => {
    const active = statusFilter === value;
    const count  = statusCounts[value] ?? 0;
    const colours = {
      slate:  active ? "bg-white/15    text-white"        : "bg-white/5    text-white/50    hover:bg-white/10",
      amber:  active ? "bg-amber-500/30 text-amber-200"   : "bg-amber-500/10 text-amber-300/70 hover:bg-amber-500/20",
      green:  active ? "bg-emerald-500/30 text-emerald-100" : "bg-emerald-500/10 text-emerald-300/70 hover:bg-emerald-500/20",
      gray:   active ? "bg-white/10 text-white/60"        : "bg-white/5 text-white/35 hover:bg-white/10",
    };
    return (
      <button
        key={value}
        onClick={() => setStatusFilter(value)}
        className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${colours[color]}`}
      >
        {label} <span className="opacity-60 ml-0.5">{count}</span>
      </button>
    );
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60">
            Review &amp; Import
          </h2>
          <p className="text-xs text-white/35 mt-0.5">
            Every paper JSON in your repo and its DB sync status. Review, then import when a paper is complete.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs
                     text-white/60 hover:bg-white/10 disabled:opacity-40 transition-colors"
        >
          {loading ? "Loading…" : "↻ Refresh"}
        </button>
      </div>

      {/* ── Filters ──────────────────────────────────────────────────────────── */}
      {papers.length > 0 && (
        <div className="px-6 py-3 border-b border-white/10 space-y-3 bg-black/10">
          {/* Status pills + search (one row) */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap items-center gap-1.5">
              {statusButton("all",          "All",           "slate")}
              {statusButton("needs_import", "Needs import",  "amber")}
              {statusButton("in_sync",      "In sync",       "green")}
              {statusButton("empty",        "Empty",         "gray")}
            </div>
            <div className="relative flex-1 min-w-[180px]">
              <svg
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/35"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                  d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search papers…"
                className="w-full rounded-lg border border-white/15 bg-white/5 pl-9 pr-9 py-3 text-xs text-white
                           placeholder-white/35 outline-none transition-colors
                           focus:border-[#2e92b0] focus:bg-white/10"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center
                             rounded-full text-white/40 hover:bg-white/10 hover:text-white/80 transition-colors"
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Filter dropdowns */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-2.5 text-xs text-white
                         outline-none transition-colors focus:border-[#2e92b0] focus:bg-white/10"
            >
              {subjectOptions.map((o) => (
                <option key={o} value={o} className="bg-[#133042]">{o === "all" ? "All subjects" : o}</option>
              ))}
            </select>
            <select
              value={examTypeFilter}
              onChange={(e) => setExamTypeFilter(e.target.value)}
              className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-2.5 text-xs text-white
                         outline-none transition-colors focus:border-[#2e92b0] focus:bg-white/10"
            >
              {examTypeOptions.map((o) => (
                <option key={o} value={o} className="bg-[#133042]">{o === "all" ? "All exam types" : o}</option>
              ))}
            </select>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-2.5 text-xs text-white
                         outline-none transition-colors focus:border-[#2e92b0] focus:bg-white/10"
            >
              {yearOptions.map((o) => (
                <option key={o} value={o} className="bg-[#133042]">{o === "all" ? "All years" : o}</option>
              ))}
            </select>
          </div>

          {anyFilterActive && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-white/40">
                Showing {filteredPapers.length} of {papers.length}
              </span>
              <button
                onClick={clearFilters}
                className="text-white/50 hover:text-white/80 transition-colors"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      )}

      {errMsg && (
        <div className="px-6 py-3 bg-red-500/10 border-b border-red-500/20 text-xs text-red-300">
          {errMsg}
        </div>
      )}

      {!loading && papers.length === 0 && (
        <div className="px-6 py-8 text-center text-sm text-white/40">
          No JSON papers found yet — upload a paper in the Upload PDF tab to get started.
        </div>
      )}

      {!loading && papers.length > 0 && filteredPapers.length === 0 && (
        <div className="px-6 py-8 text-center text-sm text-white/40">
          No papers match these filters.{" "}
          <button onClick={clearFilters} className="underline hover:text-white/60">
            Clear filters
          </button>
        </div>
      )}

      <div className="divide-y divide-white/5">
        {filteredPapers.map((p) => {
          const key      = `${p.subject}-${p.year}-${p.paper}`;
          const isBusy   = importing === key;
          const inSync   = p.status === "in_sync";
          const empty    = p.status === "empty";
          const toImport = p.questions_in_file - p.questions_in_db;

          const statusLabel =
            empty   ? "empty"     :
            inSync  ? "in sync"   : `${toImport} to import`;
          const statusColor =
            empty   ? "slate"  :
            inSync  ? "green"  : "amber";

          return (
            <div key={key} className="px-6 py-4 flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white/85 font-medium">
                  {p.subject} · {p.exam_type} · {p.year} · Paper {p.paper}
                </p>
                <p className="text-xs text-white/35 mt-0.5 font-mono truncate">
                  {p.json_path}
                </p>
                <div className="mt-1.5 flex items-center gap-2 text-xs text-white/45">
                  <span>{p.questions_in_file} in file</span>
                  <span className="text-white/25">·</span>
                  <span>{p.questions_in_db} in DB</span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <StatusPill color={statusColor}>{statusLabel}</StatusPill>
                {!inSync && !empty && (
                  <button
                    onClick={() => handleImport(p)}
                    disabled={isBusy}
                    className="rounded-lg bg-sky-600 hover:bg-sky-500 px-3 py-1.5 text-xs font-medium
                               text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {isBusy ? "Importing…" : "Import to DB →"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────

const RoboAdmin = () => {
  // ── Form state ───────────────────────────────────────────────────────────────
  const [subject,   setSubject]   = useState("Mathematics");
  const [examType,  setExamType]  = useState("BGCSE");
  const [year,      setYear]      = useState(YEARS[0]);
  const [paper,     setPaper]     = useState("1");

  // ── Build targets from the backend (drives Subject + Exam Type) ───────────────
  // GET /api/admin/robo/options returns EVERY subject in the DB, each with the
  // exam-type bands the DB has for it and a has_parser flag per band. We show all
  // of them; if the chosen subject + exam-type has no parser instructions we warn
  // (and block parsing) rather than hiding it — so you can see what's queued up.
  const [buildOptions, setBuildOptions] = useState([]); // [{ subject_name, exam_types: [{ exam_type, has_parser }] }, ...]

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/admin/robo/options`)
      .then(({ data }) => setBuildOptions(Array.isArray(data?.subjects) ? data.subjects : []))
      .catch(() => setBuildOptions([]));
  }, []);

  // Subject options = every subject the DB knows about (fallback if the call fails).
  const subjectOptions = useMemo(() => {
    const names = buildOptions.map((s) => s.subject_name).filter(Boolean);
    return names.length ? names : FALLBACK_SUBJECTS;
  }, [buildOptions]);

  // The exam-type bands for the currently-selected subject (each with its parser flag).
  const currentBands = useMemo(
    () => buildOptions.find((s) => s.subject_name === subject)?.exam_types ?? [],
    [buildOptions, subject]
  );

  // Exam types cascade off the chosen subject — the bands that exist in the DB
  // for it. Shown even when unsupported; the warning below flags those.
  const examTypeOptions = useMemo(() => {
    const types = currentBands.map((b) => b.exam_type).filter(Boolean);
    return types.length ? types : FALLBACK_EXAM_TYPES;
  }, [currentBands]);

  // Whether parser instructions exist for the exact subject + exam-type chosen.
  // Unknown/transitional combos default to true so we don't flash a false warning;
  // the backend still hard-blocks anything genuinely unparseable.
  const hasParser = useMemo(() => {
    const band = currentBands.find((b) => b.exam_type === examType);
    return band ? band.has_parser : true;
  }, [currentBands, examType]);

  // Keep the selected subject / exam type valid as options load or change.
  useEffect(() => {
    if (subjectOptions.length && !subjectOptions.includes(subject)) {
      setSubject(subjectOptions[0]);
    }
  }, [subjectOptions, subject]);

  useEffect(() => {
    if (examTypeOptions.length && !examTypeOptions.includes(examType)) {
      setExamType(examTypeOptions[0]);
    }
  }, [examTypeOptions, examType]);

  // The DB key this build will write under — derived live, shown read-only.
  const docExternalId =
    `${subject.toLowerCase().replace(/\s+/g, "_")}_${examType.toLowerCase()}_${year}_${paper}`;

  // ── Image upload state ───────────────────────────────────────────────────────
  const [images,    setImages]    = useState([]);
  const [dragging,  setDragging]  = useState(false);
  const fileInputRef = useRef(null);

  // ── Pipeline state ───────────────────────────────────────────────────────────
  // step: idle | parsing | preview | building | built | error
  // (DB imports happen via the PapersPanel — separate concern, separate state.)
  const [step,         setStep]         = useState("idle");
  const [preview,      setPreview]      = useState(null);    // response from /parse
  const [buildResult,  setBuildResult]  = useState(null);    // response from /build_json
  const [errorMsg,     setErrorMsg]     = useState("");
  const [rawOutput,    setRawOutput]    = useState("");
  const [showRaw,      setShowRaw]      = useState(false);
  const [savedImages,  setSavedImages]  = useState(new Set()); // filenames saved to disk
  const [papersRefresh, setPapersRefresh] = useState(0); // bump to force PapersPanel reload
  const [activeTab,    setActiveTab]    = useState("pdf"); // "pdf" | "build" | "papers"

  const markImageSaved = useCallback((filename) => {
    setSavedImages((prev) => {
      const next = new Set(prev);
      next.add(filename);
      return next;
    });
  }, []);

  // ── Image handling ───────────────────────────────────────────────────────────

  const addFiles = useCallback((fileList) => {
    const valid = Array.from(fileList).filter((f) =>
      f.type.startsWith("image/") || f.name.match(/\.(png|jpg|jpeg|webp|gif)$/i)
    );
    if (!valid.length) return;
    setImages((prev) => {
      const existing = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...valid.filter((f) => !existing.has(f.name + f.size))];
    });
  }, []);

  const removeImage = (idx) =>
    setImages((prev) => prev.filter((_, i) => i !== idx));

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleDragOver  = (e) => { e.preventDefault(); setDragging(true);  };
  const handleDragLeave = ()  => setDragging(false);

  // ── Reset ─────────────────────────────────────────────────────────────────────

  const reset = () => {
    setImages([]);
    setStep("idle");
    setPreview(null);
    setBuildResult(null);
    setErrorMsg("");
    setRawOutput("");
    setShowRaw(false);
    setSavedImages(new Set());
  };

  // ── Parse (Step 1) ────────────────────────────────────────────────────────────

  const handleParse = async () => {
    if (!images.length) return;
    setStep("parsing");
    setErrorMsg("");
    setPreview(null);

    try {
      const formData = new FormData();
      formData.append("subject",    subject);
      formData.append("examType",   examType);
      formData.append("year",       year);
      formData.append("paper",      paper);
      images.forEach((img) => formData.append("images[]", img));

      const { data } = await axios.post(
        `${API_BASE_URL}/api/admin/robo/parse`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setPreview(data);
      setRawOutput(data.raw || "");
      // Pre-fill savedImages with anything already on disk — backend is the
      // source of truth for what's in the repo, so we don't make the user
      // re-upload images that already exist.
      setSavedImages(new Set(data.images_on_disk || []));
      setStep("preview");
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || "Unknown error";
      const raw = err?.response?.data?.raw   || "";
      setErrorMsg(msg);
      setRawOutput(raw);
      setStep("error");
    }
  };

  // ── Build JSON (Step 2 — writes file, no DB) ─────────────────────────────────

  const handleBuildJson = async () => {
    if (!preview?.payload) return;
    setStep("building");
    setErrorMsg("");

    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/admin/robo/build_json`, {
        subject,
        examType,
        year,
        paper,
        payload: preview.payload,
      });
      setBuildResult(data);
      setStep("built");
      setPapersRefresh((n) => n + 1);
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || err.message || "Unknown error");
      setStep("error");
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────────

  const isBusy = step === "parsing" || step === "building";

  return (
    <div className="min-h-screen bg-[#0d2330] text-white p-6 sm:p-10">
      <div className="max-w-3xl mx-auto space-y-8">

        {/* ── Header ──────────────────────────────────────────────────────────── */}
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">🤖 Robo Admin</h1>
          <p className="mt-1 text-sm leading-relaxed text-white/50">
            Turns a past-paper PDF into questions students can practise. Robo reads the
            paper, works out the answers and crops the diagrams, but nothing reaches a
            student until we have approved it question by question.
          </p>
        </div>

        {/* ── Tab bar ─────────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-1 border-b border-white/10">
          <button
            onClick={() => setActiveTab("pdf")}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${
              activeTab === "pdf"
                ? "text-white border-[#2e92b0]"
                : "text-white/40 border-transparent hover:text-white/70"
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              Upload PDF
              <InfoDot label="Why Upload PDF is local-only">
                <span className="font-semibold text-amber-200">Local machine only.</span>{" "}
                Reads a PDF, solves it, and writes the paper's JSON and question images
                into the repo as files we commit to git. On a deployed server those
                writes land in a container that is wiped on the next deploy, and nothing
                in a container can commit, so the work would be lost.
              </InfoDot>
            </span>
          </button>
          <button
            onClick={() => setActiveTab("build")}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${
              activeTab === "build"
                ? "text-white border-[#2e92b0]"
                : "text-white/40 border-transparent hover:text-white/70"
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              Legacy: Upload Images
              <InfoDot label="Why Legacy: Upload Images is local-only">
                <span className="font-semibold text-amber-200">Local machine only.</span>{" "}
                The original image-based parser, kept as a fallback. Like Upload PDF it
                writes JSON and images into the repo for us to commit, so a deployed
                server would discard the work.
              </InfoDot>
            </span>
          </button>
          <button
            onClick={() => setActiveTab("papers")}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-[1px] ${
              activeTab === "papers"
                ? "text-white border-[#2e92b0]"
                : "text-white/40 border-transparent hover:text-white/70"
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              Review &amp; Import
              <InfoDot label="Where Review and Import runs" align="right">
                <span className="font-semibold text-emerald-200">Works anywhere.</span>{" "}
                Unlike the other two tabs, this one writes no files. It reads the JSON
                already committed to the repo and inserts the questions into the database.
                Run it locally to fill our dev database, and on the deployed server to
                publish those questions to students.
              </InfoDot>
            </span>
          </button>
        </div>

        {/* ── PDF ingestion (primary) ─────────────────────────────────────────── */}
        {activeTab === "pdf" && (
          <PdfPipeline
            subjectOptions={buildOptions}
            onPapersChanged={() => setPapersRefresh((n) => n + 1)}
          />
        )}

        {/* ── Review & Import tab ─────────────────────────────────────────────── */}
        {activeTab === "papers" && <PapersPanel refreshKey={papersRefresh} />}

        {/* ── Main card (Legacy image-upload tab) ───────────────────────────── */}
        {activeTab === "build" && (
        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden">

          {/* Kept rather than deleted for one case only: a paper we hold as images with
              no PDF behind them. A badly cropped diagram is not that case — the PDF flow
              replaces an asset in place, so it never needs this tab. */}
          <div className="border-b border-white/10 bg-white/[0.02] px-6 py-4">
            <p className="text-xs leading-relaxed text-white/55">
              <span className="font-semibold text-white/75">The original parser.</span>{" "}
              Before Robo could read a PDF, we exported every exam page as an image and
              dropped them here. It still works exactly as it always did. Upload PDF
              replaced it because one file beats twenty screenshots, and because the PDF
              carries the page layout Robo needs to crop diagrams on its own.
            </p>
            <p className="mt-2 text-xs leading-relaxed text-white/40">
              Reach for this only when we have no PDF of the paper, just pictures of the
              pages.
            </p>
          </div>

          {/* ── Section 1: Paper selection ──────────────────────────────────── */}
          <div className="px-6 py-5 border-b border-white/10">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60">
                1 · Select Paper
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-white/45">doc_external_id:</span>
                <code className="font-mono text-xs text-[#7fd4ec] bg-[#2e92b0]/10 border border-[#2e92b0]/30
                                 rounded-md px-2.5 py-1 select-all break-all">
                  {docExternalId}
                </code>
                <span className="relative inline-flex group">
                  <span
                    aria-label="What is doc_external_id?"
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-white/30
                               text-[10px] font-semibold text-white/50 cursor-help select-none
                               group-hover:text-white/80 group-hover:border-white/50 transition-colors"
                  >
                    i
                  </span>
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute right-0 top-full z-20 mt-2 w-56 rounded-lg
                               border border-white/15 bg-[#0b1c27] px-3 py-2.5 text-[11px] leading-relaxed
                               text-white/70 shadow-xl opacity-0 invisible translate-y-1
                               group-hover:opacity-100 group-hover:visible group-hover:translate-y-0
                               transition-all duration-150"
                  >
                    The unique ID this paper is saved under, built automatically from your selections.
                  </span>
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Select label="Subject"    value={subject}  onChange={setSubject}  options={subjectOptions}  disabled={isBusy} />
              <Select label="Exam Type"  value={examType} onChange={setExamType} options={examTypeOptions} disabled={isBusy || examTypeOptions.length <= 1} />
              <Select label="Year"       value={year}     onChange={setYear}     options={YEARS}           disabled={isBusy} />
              <Select label="Paper"      value={paper}    onChange={setPaper}    options={PAPERS}          disabled={isBusy} />
            </div>

            {/* Parser-availability warning — the subject + exam-type link. */}
            {!hasParser && (
              <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3.5 py-3">
                <svg className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                    d="M12 9v3.75m0 3.75h.008M10.34 3.94l-7.5 13A1.5 1.5 0 004.14 19h15.72a1.5 1.5 0 001.3-2.06l-7.5-13a1.5 1.5 0 00-2.62 0z" />
                </svg>
                <p className="text-xs text-amber-200/90 leading-relaxed">
                  No parser instructions exist for{" "}
                  <span className="font-semibold text-amber-100">{subject}</span>{" "}
                  (<span className="font-semibold text-amber-100">{examType}</span>).
                  This subject + exam-type can't be parsed until a parser file is added under{" "}
                  <code className="font-mono text-amber-200 bg-black/20 rounded px-1 py-0.5">etc/skills/</code>{" "}
                  and registered in{" "}
                  <code className="font-mono text-amber-200 bg-black/20 rounded px-1 py-0.5">SUBJECT_CONFIG</code>.
                </p>
              </div>
            )}
          </div>

          {/* ── Section 2: Image upload ──────────────────────────────────────── */}
          <div className="px-6 py-5 border-b border-white/10">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60 mb-4">
              2 · Upload Paper Pages
            </h2>

            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => !isBusy && fileInputRef.current?.click()}
              className={`
                relative rounded-xl border-2 border-dashed px-6 py-8 text-center cursor-pointer
                transition-colors duration-200
                ${dragging
                  ? "border-[#2e92b0] bg-[#2e92b0]/10"
                  : "border-white/15 bg-white/[0.03] hover:border-white/25 hover:bg-white/5"}
                ${isBusy ? "pointer-events-none opacity-50" : ""}
              `}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/png,image/jpeg,image/jpg,image/webp"
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
                disabled={isBusy}
              />
              <div className="flex flex-col items-center gap-2 text-white/40">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm">
                  <span className="text-white/70 font-medium">Click to browse</span>
                  {" "}or drag and drop
                </p>
                <p className="text-xs">PNG, JPG, WEBP · any number of pages</p>
              </div>
            </div>

            {/* Image list */}
            {images.length > 0 && (
              <div className="mt-3 space-y-1.5">
                <p className="text-xs text-white/40 mb-2">
                  {pluralise(images.length, "image")} queued
                </p>
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 rounded-lg bg-white/5 px-3 py-2"
                  >
                    <FileIcon />
                    <span className="flex-1 text-sm text-white/80 truncate">{img.name}</span>
                    <span className="text-xs text-white/30 shrink-0">
                      {(img.size / 1024).toFixed(0)} KB
                    </span>
                    {!isBusy && (
                      <button
                        onClick={(e) => { e.stopPropagation(); removeImage(idx); }}
                        className="text-white/30 hover:text-red-400 transition-colors text-lg leading-none"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Section 3: Parse button ──────────────────────────────────────── */}
          {step !== "done" && (
            <div className="px-6 py-5">
              <button
                onClick={handleParse}
                disabled={images.length === 0 || isBusy || !hasParser}
                className="
                  w-full rounded-xl py-3 px-6 font-medium text-sm transition-all duration-200
                  bg-[#2e92b0] hover:bg-[#3aadd0] text-white
                  disabled:opacity-40 disabled:cursor-not-allowed
                  relative overflow-hidden
                "
              >
                {step === "parsing" ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Claude is reading the paper…
                  </span>
                ) : (
                  `Parse ${images.length > 0 ? pluralise(images.length, "page") : "pages"} with Claude →`
                )}
              </button>

              {step === "parsing" && (
                <p className="text-center text-xs text-white/30 mt-2">
                  This can take 30–90 seconds depending on page count
                </p>
              )}
            </div>
          )}
        </div>
        )}

        {/* ── Preview card ─────────────────────────────────────────────────────── */}
        {activeTab === "build" && (step === "preview" || step === "building") && preview && (
          <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60">
                3 · Preview
              </h2>
              <button
                onClick={() => setShowRaw((v) => !v)}
                className="text-xs text-white/30 hover:text-white/60 transition-colors"
              >
                {showRaw ? "Hide raw output" : "Show raw output"}
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* File path */}
              <div className="rounded-lg bg-black/20 px-4 py-3">
                <p className="text-xs text-white/40 mb-0.5">Target file</p>
                <p className="text-sm font-mono text-white/80">{preview.json_path}</p>
                <p className="text-xs text-white/30 mt-1">
                  {preview.file_exists
                    ? `File exists · ${preview.existing_question_count} question(s) already in file`
                    : "New file — will be created"}
                </p>
              </div>

              {/* Stats row */}
              <div className="flex flex-wrap gap-2">
                <StatusPill color="blue">
                  {pluralise(preview.parsed_question_count, "question")} parsed
                </StatusPill>
                {preview.new_numbers_for_file.length > 0 && (
                  <StatusPill color="green">
                    {pluralise(preview.new_numbers_for_file.length, "new")} for file
                  </StatusPill>
                )}
                {preview.new_numbers_for_db.length > 0 && (
                  <StatusPill color="green">
                    {pluralise(preview.new_numbers_for_db.length, "new")} for DB
                  </StatusPill>
                )}
                {preview.skipped_in_file.length > 0 && (
                  <StatusPill color="amber">
                    {pluralise(preview.skipped_in_file.length, "duplicate")} in file
                  </StatusPill>
                )}
                {preview.skipped_in_db.length > 0 && (
                  <StatusPill color="amber">
                    {pluralise(preview.skipped_in_db.length, "duplicate")} in DB
                  </StatusPill>
                )}
              </div>

              {/* New question numbers */}
              {preview.new_numbers_for_file.length > 0 && (
                <div>
                  <p className="text-xs text-white/40 mb-1.5">Questions to be added</p>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.new_numbers_for_file.map((n) => (
                      <span key={n} className="rounded-md bg-emerald-500/10 border border-emerald-500/20
                                               px-2 py-0.5 text-xs font-mono text-emerald-300">
                        Q{n}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Skipped numbers */}
              {preview.skipped_in_file.length > 0 && (
                <div>
                  <p className="text-xs text-white/40 mb-1.5">Already in file — will be skipped</p>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.skipped_in_file.map((n) => (
                      <span key={n} className="rounded-md bg-white/5 border border-white/10
                                               px-2 py-0.5 text-xs font-mono text-white/40">
                        Q{n}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Unsupported questions */}
              {preview.unsupported_questions?.length > 0 && (
                <div>
                  <p className="text-xs text-white/40 mb-1.5">Skipped by Claude (unsupported type)</p>
                  <div className="space-y-1">
                    {preview.unsupported_questions.map((q) => (
                      <div key={q.number} className="flex items-start gap-2 text-xs">
                        <span className="rounded-md bg-amber-500/10 border border-amber-500/20
                                         px-2 py-0.5 font-mono text-amber-300 shrink-0">
                          Q{q.number}
                        </span>
                        <span className="text-white/40">{q.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Raw output toggle */}
              {showRaw && rawOutput && (
                <div>
                  <p className="text-xs text-white/40 mb-1.5">Raw Claude output</p>
                  <pre className="rounded-lg bg-black/30 p-4 text-xs text-white/50 overflow-x-auto
                                  max-h-64 font-mono whitespace-pre-wrap">
                    {rawOutput}
                  </pre>
                </div>
              )}
            </div>

            {/* ── Images required (gating section) ──────────────────────────── */}
            {preview.images_needed?.length > 0 && (() => {
              const total       = preview.images_needed.length;
              const savedCount  = preview.images_needed.filter((i) => savedImages.has(i.filename)).length;
              const allSaved    = savedCount === total;

              return (
                <div className="px-6 py-5 border-t border-white/10 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-white/60">
                      Required images
                    </h3>
                    <StatusPill color={allSaved ? "green" : "amber"}>
                      {savedCount} / {total} saved
                    </StatusPill>
                  </div>
                  <p className="text-xs text-white/35">
                    Each question below depends on a diagram. Files already in the repo are pre-checked —
                    you only need to upload the missing ones before the JSON can be built.
                  </p>
                  <div className="space-y-2 pt-1">
                    {preview.images_needed.map((item) => {
                      const onDisk = (preview.images_on_disk || []).includes(item.filename);
                      return (
                        <ImageUploadRow
                          key={item.filename}
                          item={item}
                          subject={subject}
                          examType={examType}
                          year={year}
                          paper={paper}
                          isSaved={savedImages.has(item.filename)}
                          wasAlreadyOnDisk={onDisk}
                          onSaved={markImageSaved}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* ── Build JSON button (gated, no DB write) ─────────────────────── */}
            {(() => {
              const totalImagesNeeded = preview.images_needed?.length ?? 0;
              const allImagesSaved    = totalImagesNeeded === 0
                || preview.images_needed.every((i) => savedImages.has(i.filename));
              const nothingNew        = preview.new_numbers_for_file.length === 0;
              const buttonDisabled    = step === "building" || nothingNew || !allImagesSaved;

              let label;
              if (step === "building") {
                label = (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Building JSON…
                  </span>
                );
              } else if (nothingNew) {
                label = "Nothing new to add — everything is already in the JSON";
              } else if (!allImagesSaved) {
                const remaining = totalImagesNeeded - preview.images_needed.filter((i) => savedImages.has(i.filename)).length;
                label = `🔒 Save ${pluralise(remaining, "more image")} to continue`;
              } else {
                label = `Build JSON · ${pluralise(preview.new_numbers_for_file.length, "question")} →`;
              }

              return (
                <div className="px-6 py-5 border-t border-white/10">
                  <button
                    onClick={handleBuildJson}
                    disabled={buttonDisabled}
                    className="w-full rounded-xl py-3 px-6 font-medium text-sm transition-all duration-200
                               bg-emerald-600 hover:bg-emerald-500 text-white
                               disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {label}
                  </button>
                  <p className="text-center text-xs text-white/35 mt-2">
                    {!allImagesSaved && totalImagesNeeded > 0 && step !== "building"
                      ? "All required images must be saved to disk before the JSON can be built."
                      : "Writes the JSON file only — DB import is a separate step so you can quality-check the file first."}
                  </p>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Built card (JSON written — DB import happens via Review & Import tab) ── */}
        {activeTab === "build" && step === "built" && buildResult && (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-6 py-6 space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📄</span>
              <div>
                <p className="font-semibold text-emerald-300">JSON built · DB unchanged</p>
                <p className="text-sm text-white/50 mt-0.5">{buildResult.json_path}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 text-sm">
              <div className="rounded-lg bg-white/5 px-4 py-2 text-center">
                <p className="text-xs text-white/40">Added to file</p>
                <p className="text-xl font-semibold text-emerald-300">{buildResult.questions_added_to_file}</p>
              </div>
              <div className="rounded-lg bg-white/5 px-4 py-2 text-center">
                <p className="text-xs text-white/40">Total in file</p>
                <p className="text-xl font-semibold text-white/80">{buildResult.total_questions_in_file}</p>
              </div>
            </div>

            <div className="rounded-lg bg-black/20 px-4 py-3 text-xs text-white/60 leading-relaxed">
              When the paper is complete, switch to the <span className="font-semibold text-white/80">Review &amp; Import</span> tab
              and click <span className="font-semibold text-white/80">Import to DB</span> on this row. Until then you can
              keep adding pages, edit the JSON file by hand, or come back tomorrow.
            </div>

            <button
              onClick={reset}
              className="w-full rounded-xl py-3 px-6 font-medium text-sm bg-white/10
                         hover:bg-white/15 text-white transition-colors"
            >
              Add more pages →
            </button>
          </div>
        )}

        {/* ── Error card ────────────────────────────────────────────────────────── */}
        {activeTab === "build" && step === "error" && (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-6 py-6 space-y-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl shrink-0">⚠️</span>
              <div className="min-w-0">
                <p className="font-semibold text-red-300">Something went wrong</p>
                <p className="text-sm text-white/60 mt-1 break-words">{errorMsg}</p>
              </div>
            </div>

            {rawOutput && (
              <div>
                <button
                  onClick={() => setShowRaw((v) => !v)}
                  className="text-xs text-white/30 hover:text-white/60 transition-colors"
                >
                  {showRaw ? "Hide" : "Show"} raw Claude output
                </button>
                {showRaw && (
                  <pre className="mt-2 rounded-lg bg-black/30 p-4 text-xs text-white/50
                                  overflow-x-auto max-h-64 font-mono whitespace-pre-wrap">
                    {rawOutput}
                  </pre>
                )}
              </div>
            )}

            <button
              onClick={() => setStep(buildResult ? "built" : preview ? "preview" : "idle")}
              className="w-full rounded-xl py-3 px-6 font-medium text-sm bg-white/10
                         hover:bg-white/15 text-white transition-colors"
            >
              ← Go back
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default RoboAdmin;
